import type { EmailCompositionValue } from '@kula-ai/components';
import type { SelectOption } from '@kula-ai/components';
import { mergeEmailState } from '@kula-ai/components';
import { EMAIL_COMPOSITION_TO_MODE, patchEmailComposition } from './emailUtils';

/** Maps ATS template attachment rows to Kula `Editor` attachment chips (read-only URLs). */
export function mapTemplateAttachmentsToEditorItems(raw: unknown): Array<{
  key: string;
  name: string;
  size?: number;
  mimeType?: string;
  url?: string;
}> {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((a: Record<string, unknown>, i: number) => ({
    key: String(a.id ?? i),
    name: String(a.filename ?? 'file'),
    size: typeof a.byte_size === 'number' ? a.byte_size : undefined,
    mimeType: typeof a.content_type === 'string' ? a.content_type : undefined,
    url: typeof a.url === 'string' ? a.url : undefined,
  }));
}

function findTemplateOption(
  templateId: string | null,
  templateOptions: SelectOption[]
): SelectOption | undefined {
  if (!templateId) {
    return undefined;
  }
  return templateOptions.find((o) => o.value === templateId);
}

/**
 * When the user picks a different template row, Kula `Email` updates `content` from `option.data.body`.
 * We still merge `subject` (and body fallback if the row had empty HTML) from the same option.
 */
export function applyTemplateMetadataOnChange(
  previous: EmailCompositionValue,
  next: EmailCompositionValue,
  templateOptions: SelectOption[]
): EmailCompositionValue {
  if (previous.template === next.template) {
    return next;
  }
  if (!next.template) {
    return next;
  }
  const opt = findTemplateOption(next.template, templateOptions);
  if (!opt) {
    return next;
  }
  const patch: Partial<EmailCompositionValue> = {};
  const subj = opt.data?.subject;
  if (typeof subj === 'string') {
    patch.subject = subj;
  }
  const body = opt.data?.body;
  if (typeof body === 'string' && body.trim() !== '') {
    patch.content = body;
  }
  return Object.keys(patch).length
    ? mergeEmailState(next, patch, EMAIL_COMPOSITION_TO_MODE)
    : next;
}

/** Prefill composition when applying the first fetched template (or programmatic default). */
export function compositionFromTemplateOption(
  base: EmailCompositionValue,
  option: SelectOption
): EmailCompositionValue {
  const body = String(option.data?.body ?? '');
  const subject = String(option.data?.subject ?? '');
  return patchEmailComposition(base, {
    template: option.value,
    content: body,
    subject,
  });
}
